// The depth tints of chart card 09 in the look of mockup round 116
// (R2713, R2726, ui-map-interface.md §17.3.09, v2-the-deep.md §13).
//
// The layer owns no geometry. The engine's depth plane holds the band of
// each cell, and ONE full-world quad paints the tint, the shoal stipple
// (card 71) and the edge seam (card 72) (`shaders/depth.ts`). The chart
// piece `depth_tints` hands the band field over when the hello lands, on a
// survey and on a reading change, so a frame at rest costs one quad and the
// hull feed (§10). The scene hands the look and the gates of the three
// pieces over on every frame; the stepped look draws neither mark.
//
// **THE DEPTH HEAT LOOK** (R2870). While the DEPTH heat map is on, the
// scene turns the heat look on (`setHeatLook`): each band its own ink and
// alpha, a hard step, and a contour on the shallower side of each band
// edge. The stipple and the seam do not draw in it.
//
// **IT DRAWS WHAT THE WIRE SENDS** (v2 §14.1 rule 5). The band field is
// `Hello.depth` (R2609), nil while the flag `depth` is off, and the layer
// stands invisible without it. It reads no flag.
//
// **THE SEAM FOLLOWS THE OWN HULLS.** The scene feeds the own hulls of each
// frame; a frame that fed none (a replay film has the glass) draws no seam,
// because a film shows no hull of today.
//
// The pure halves below — the hash, the stipple pick, the seam fade and
// the hull pick — are the twins of the shader, and the tests read them.

import type { DataTexture } from '../gfx/types';
import type { WorldEnv, WorldPass } from '../gfx/worldquad';
import { UniformValues } from '../gfx/uniforms';
import type { DrawCtx, FrameInfo, Layer, ViewRect } from '../layer';
import type { FleetInfo } from '../../render/fleet-info';
import type { ChartReading } from '../chart/context';
import { CHART } from '../../gen/presentation';
import { rgb } from '../../render/color';
import { CHART_INK, inkOf } from '../chart/pieces/shared';
import { DEPTH_UNIFORMS, depthProgram } from '../shaders/depth';
import {
  DEPTH_LOOK_DEFAULT, SEAM_HULL_SLOTS, STIPPLE_MAX, depthHeatCfg, depthLookCfg, type DepthLook,
} from '../../depthlook';

const T = CHART.pieces.depth_tints;
const STIPPLE = CHART.pieces.shoal_stipple;
const SEAM = CHART.pieces.edge_seam;

/** The integer hash of the stipple pick: `dpHash` of the shader. */
export function hash32(i: number): number {
  let h = Math.imul(i ^ 0x9e3779b9, 2654435761) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519) >>> 0;
  return (h ^ (h >>> 13)) >>> 0;
}

/** How many dots a cell of a band carries: the whole dots of the band,
 *  and one more where the hash of the cell falls under the fraction. */
export function stippleCount(cell: number, band: number): number {
  const per = STIPPLE.arr.per_cell[band - 1] ?? 0;
  let n = Math.floor(per);
  if (hash32((cell * 31 + 97) >>> 0) % 1000 < (per - n) * 1000) n += 1;
  return Math.min(n, STIPPLE_MAX);
}

/**
 * The dots of one cell, as x, y pairs from the cell centre in world px:
 * `stippleCov` of the shader. A dot stands at a hashed bearing and at a
 * hashed distance under `spread_px`, the square root spreading the
 * dots evenly over the disc.
 */
export function stippleDots(cell: number, band: number): number[] {
  const out: number[] = [];
  const n = stippleCount(cell, band);
  for (let k = 0; k < n; k++) {
    const h = hash32((cell * 31 + k) >>> 0);
    const h2 = hash32((h + 11) >>> 0);
    const ang = (h % 6283) / 1000;
    const d = Math.sqrt((h2 % 1000) / 1000) * STIPPLE.fig.spread_px;
    out.push(Math.cos(ang) * d, Math.sin(ang) * d);
  }
  return out;
}

/** The alpha of the seam at `d` world px from the nearest own hull:
 *  (1 - d / R)², and 0 past the radius (`hullNear` of the shader). */
export function seamFade(d: number): number {
  const t = Math.max(0, 1 - d / SEAM.fig.radius_px);
  return t * t;
}

/**
 * Picks the own hulls that light a seam this frame into `out`, two to a
 * vec4: the own hulls within the seam radius of the view, the nearest to
 * the middle of the view first, at most `SEAM_HULL_SLOTS`. Answers the
 * count. `order` is scratch, so a frame allocates nothing.
 */
export function pickSeamHulls(
  fleets: readonly FleetInfo[], view: ViewRect, out: number[], order: number[],
): number {
  const r = SEAM.fig.radius_px;
  const mx = (view.x0 + view.x1) / 2, my = (view.y0 + view.y1) / 2;
  order.length = 0;
  for (let i = 0; i < fleets.length; i++) {
    const f = fleets[i];
    if (!f.own || f.alpha <= 0) continue;
    if (f.x < view.x0 - r || f.x > view.x1 + r || f.y < view.y0 - r || f.y > view.y1 + r) continue;
    order.push(i);
  }
  if (order.length > SEAM_HULL_SLOTS) {
    const d2 = (i: number) => (fleets[i].x - mx) ** 2 + (fleets[i].y - my) ** 2;
    order.sort((a, b) => d2(a) - d2(b) || a - b);
  }
  const n = Math.min(order.length, SEAM_HULL_SLOTS);
  out.fill(0);
  for (let k = 0; k < n; k++) {
    const f = fleets[order[k]];
    out[2 * k] = f.x;
    out[2 * k + 1] = f.y;
  }
  return n;
}

/** Writes the band field into the plane: the band of each cell, and 0
 *  where the field holds none. A null field writes 0 everywhere. */
export function writeBands(
  plane: { data: Uint8Array | Float32Array; markDirty(): void }, bands: Uint8Array | null,
): void {
  const out = plane.data;
  out.fill(0);
  if (bands) {
    const n = Math.min(out.length, bands.length);
    for (let c = 0; c < n; c++) out[c] = bands[c] <= 4 ? bands[c] : 0;
  }
  plane.markDirty();
}

/** What the layer needs of the engine. */
export interface DepthEngine {
  readonly depth: DataTexture;
  readonly env: WorldEnv;
  newWorldPass(program: ReturnType<typeof depthProgram>): WorldPass;
}

export class DepthLayer implements Layer {
  readonly name = 'depth';

  private readonly pass: WorldPass;
  readonly u = new UniformValues(DEPTH_UNIFORMS);
  /** The chart piece shows the tints. */
  private on = false;
  /** The plane holds a band field. */
  private hasBands = false;
  private shown = true;
  private reading: ChartReading = 'modern';
  /** The look the scene asks for, and the gates of the two marks. */
  private want: DepthLook = DEPTH_LOOK_DEFAULT;
  private stippleGate = false;
  private seamGate = false;
  /** The DEPTH heat map is on: the depth heat look draws (R2870). */
  private heat = false;
  /** The look the uniforms hold, or null before the first write. */
  private applied: DepthLook | null = null;
  /** The marks the uniforms hold: the gate of each piece, in the
   *  feathered look alone. */
  private stippleOn = false;
  private seamOn = false;
  /** Two own hulls a vec4, and the scratch of the pick. */
  private readonly hulls: number[] = new Array<number>(SEAM_HULL_SLOTS * 2).fill(0);
  private readonly order: number[] = [];
  /** The scene fed the hulls since the last draw. */
  private fed = false;

  constructor(private readonly engine: DepthEngine) {
    this.pass = engine.newWorldPass(depthProgram());
    this.pass.setExtra((p) => this.u.apply(p));
  }

  /** The layer draws while the piece shows it, a field stands and nothing
   *  hides it. */
  get visible(): boolean { return this.on && this.hasBands && this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /** The look the uniforms hold. The tests read it. */
  get look(): DepthLook | null { return this.applied; }

  /** The uniforms hold the depth heat look. The tests read it. */
  get heatLook(): boolean { return this.heat; }

  /** The edge seam draws: the feathered look and the gate of card 72.
   *  The tests read it. */
  get seamDrawn(): boolean { return this.seamOn; }

  /**
   * The gate of the chart piece, written by the scene every frame: the
   * tints draw while the piece shows. The field stays in the plane, so a
   * piece that comes back draws at once.
   */
  setGate(on: boolean): void { this.on = on; }

  /**
   * The look the theme picks (flipped by the key `\`) and the gates of the
   * shoal stipple (card 71) and the edge seam (card 72), written by the
   * scene every frame (R2726). Answers whether the look changed, which
   * the band swatches of the legend card show. The legend shows no seam
   * (R2831).
   */
  setMarks(look: DepthLook, stipple: boolean, seam: boolean): boolean {
    if (look === this.want && stipple === this.stippleGate && seam === this.seamGate) return false;
    const lookBefore = this.applied;
    this.want = look;
    this.stippleGate = stipple;
    this.seamGate = seam;
    this.applied = null;
    this.applyLook();
    return this.applied !== lookBefore;
  }

  /**
   * The depth heat look of the DEPTH heat map (R2870), written by the
   * scene every frame. Answers whether it changed, which the legend card
   * shows.
   */
  setHeatLook(on: boolean): boolean {
    if (on === this.heat) return false;
    this.heat = on;
    this.applied = null;
    this.applyLook();
    return true;
  }

  /**
   * The chart piece's one call (§17.3.09): the band field and the reading.
   * A field written in place by a survey is written again, so the call
   * always copies.
   */
  setField(reading: ChartReading, bands: Uint8Array | null): void {
    this.hasBands = bands !== null && bands.length > 0;
    writeBands(this.engine.depth, this.hasBands ? bands : null);
    if (reading !== this.reading) {
      this.reading = reading;
      this.applied = null;
    }
    this.applyLook();
  }

  /** The own hulls of this frame. The seam stands near them alone. */
  feedHulls(fleets: readonly FleetInfo[], view: ViewRect): void {
    this.fed = true;
    const n = this.seamOn ? pickSeamHulls(fleets, view, this.hulls, this.order) : 0;
    this.u.set('uDpHullN', n);
    this.u.set('uDpHulls', this.hulls);
  }

  update(_frame: FrameInfo): void {
    // The scene writes the look and the gates (`setMarks`).
  }

  draw(ctx: DrawCtx): void {
    if (!this.fed) this.u.set('uDpHullN', 0);
    this.fed = false;
    this.pass.draw(ctx.cam, this.engine.env);
  }

  dispose(): void { this.pass.dispose(); }

  /** Writes the uniforms of the look and the marks in the reading. */
  private applyLook(): void {
    const look = this.want;
    if (look === this.applied) return;
    this.applied = look;
    const cfg = depthLookCfg(look, this.reading);
    const r = T[this.reading], st = STIPPLE[this.reading], sm = SEAM[this.reading];
    // The stepped look is the tint step alone (the lead's ruling, R2726),
    // and the heat look draws no mark either (R2870).
    const heat = this.heat;
    this.stippleOn = !heat && cfg.feathered && this.stippleGate;
    this.seamOn = !heat && cfg.feathered && this.seamGate;
    this.u.set('uDpSoft', !heat && cfg.feathered ? 1 : 0);
    this.u.set('uDpHeat', heat ? 1 : 0);
    const hc = depthHeatCfg(this.reading);
    this.u.set('uDpHeatInk', hc.inks.flatMap((ink, i) => [...rgb(ink), hc.alphas[i]]));
    this.u.set('uDpEdgeA', hc.edgeAlpha);
    this.u.set('uDpEdgeW', hc.edgePx);
    this.u.set('uDpAlpha', cfg.alphas);
    this.u.set('uDpInk', rgb(inkOf(r.ink, CHART_INK.deep)));
    this.u.set('uDpMark', rgb(inkOf(r.fill, CHART_INK.pale)));
    this.u.set('uDpStipple', this.stippleOn ? 1 : 0);
    this.u.set('uDpStipR', st.fig.dot_r);
    this.u.set('uDpStipA', st.fig.dot_alpha * st.alpha);
    this.u.set('uDpSeam', this.seamOn ? 1 : 0);
    this.u.set('uDpGlowA', sm.fig.glow_alpha * sm.alpha);
    this.u.set('uDpLineA', sm.fig.line_alpha * sm.alpha);
    if (!this.seamOn) this.u.set('uDpHullN', 0);
  }
}
